import { useContext, useEffect, useState } from 'react';
import { ArrowLeft, Minus, Pencil, Plus, ShoppingBag, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CartContext from '../../Context/Cart/CartContext';
import PaymentDrawer from '../Payments';
import ItemCustomization from '../productDetails/ItemCustomization';
import { formatCurrency } from '../../Utils';

const itemName = (item) => item.item_name || item.name || 'Menu item';
const itemImage = (item) => item.image_url || item.image || item.item_image || '';

export default function PublicMenuOrderPanel({
  open,
  onClose,
  tableNumber,
  merchantId,
  merchantName,
  accentColor,
}) {
  const navigate = useNavigate();
  const { cartItems, itemCount, total, increase, decrease, removeFromCart, addToCart } = useContext(CartContext);
  const [showPayment, setShowPayment] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const hasTable = tableNumber && tableNumber !== '0' && tableNumber !== 'Online';
  const [orderType, setOrderType] = useState(hasTable ? 'dine_in' : 'takeaway');

  useEffect(() => {
    if (!open) {
      setShowPayment(false);
      setEditingItem(null);
    }
  }, [open]);

  useEffect(() => {
    setOrderType(hasTable ? 'dine_in' : 'takeaway');
  }, [hasTable, tableNumber]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const beginEdit = (item) => {
    const selections = {};
    (item.selectedOptions || []).forEach((option) => {
      if (option.optionId) selections[option.groupId] = option.optionId;
    });
    setEditingItem({
      item,
      oldItemId: item.item_public_id,
      initialSelections: selections,
      initialInstructions: item.specialInstructions || '',
      initialQuantity: item.quantity,
    });
  };

  const saveEdit = (newCartItem) => {
    removeFromCart({ item_public_id: editingItem.oldItemId });
    addToCart(newCartItem);
    setEditingItem(null);
  };

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        className="absolute inset-0 hidden bg-black/40 sm:block"
        onClick={onClose}
        aria-label="Close order summary"
      />

      <section className="absolute inset-0 flex flex-col bg-white sm:inset-y-0 sm:left-auto sm:right-0 sm:w-[460px] sm:max-w-[92vw] sm:border-l sm:border-neutral-200 sm:shadow-2xl">
        {showPayment ? (
          <PaymentDrawer
            embedded
            onClose={() => setShowPayment(false)}
            table={tableNumber}
            merchantId={merchantId}
            merchantName={merchantName}
            orderType={orderType}
            onSuccess={(orderId) => navigate('/my-account/orders', { state: { orderId } })}
          />
        ) : (
          <>
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-neutral-200 px-4 sm:px-5">
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-neutral-700 hover:bg-neutral-100"
            aria-label="Back to menu"
          >
            <ArrowLeft className="h-5 w-5 sm:hidden" />
            <X className="hidden h-5 w-5 sm:block" />
          </button>
          <div className="min-w-0 flex-1">
            <h2 className="text-[17px] font-semibold text-neutral-950">Your order</h2>
            <p className="truncate text-[12px] text-neutral-500">
              {hasTable ? `Table ${tableNumber}` : 'Collection order'} · {itemCount} item{itemCount === 1 ? '' : 's'}
            </p>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5">
          {cartItems.length === 0 ? (
            <div className="flex min-h-[55vh] flex-col items-center justify-center px-6 text-center">
              <ShoppingBag className="h-8 w-8 text-neutral-300" />
              <h3 className="mt-4 text-[16px] font-semibold text-neutral-900">Your order is empty</h3>
              <p className="mt-1 max-w-[250px] text-[13px] leading-5 text-neutral-500">Choose something from the menu and it will appear here.</p>
              <button type="button" onClick={onClose} className="mt-5 h-10 rounded-xl border border-neutral-300 px-4 text-[13px] font-semibold text-neutral-800">
                Browse menu
              </button>
            </div>
          ) : (
            <>
              <div className="mb-5 grid grid-cols-2 gap-1 rounded-xl bg-neutral-100 p-1">
                <button
                  type="button"
                  onClick={() => setOrderType('dine_in')}
                  disabled={!hasTable}
                  className="h-10 rounded-lg text-[13px] font-semibold disabled:cursor-not-allowed disabled:opacity-40"
                  style={orderType === 'dine_in' ? { backgroundColor: '#fff', color: '#171717', boxShadow: '0 1px 3px rgba(0,0,0,.08)' } : { color: '#737373' }}
                >
                  {hasTable ? `Dine in · ${tableNumber}` : 'Dine in'}
                </button>
                <button
                  type="button"
                  onClick={() => setOrderType('takeaway')}
                  className="h-10 rounded-lg text-[13px] font-semibold"
                  style={orderType === 'takeaway' ? { backgroundColor: '#fff', color: '#171717', boxShadow: '0 1px 3px rgba(0,0,0,.08)' } : { color: '#737373' }}
                >
                  Takeaway
                </button>
              </div>

              <div className="divide-y divide-neutral-200">
                {cartItems.map((item) => (
                  <article key={item.item_public_id} className="flex gap-3 py-4 first:pt-0">
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-neutral-100">
                      {itemImage(item) ? <img src={itemImage(item)} alt="" className="h-full w-full object-cover" /> : null}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="text-[14px] font-semibold leading-5 text-neutral-950">{itemName(item)}</h3>
                          {(item.selectedOptions || []).length > 0 && (
                            <p className="mt-0.5 line-clamp-2 text-[12px] leading-4 text-neutral-500">
                              {item.selectedOptions.map((option) => option.optionName).filter(Boolean).join(', ')}
                            </p>
                          )}
                        </div>
                        <span className="shrink-0 text-[13px] font-semibold text-neutral-900">{formatCurrency(Number(item.price || 0) * Number(item.quantity || 1))}</span>
                      </div>

                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex h-9 items-center rounded-lg border border-neutral-200">
                          <button type="button" onClick={() => decrease(item)} disabled={item.quantity <= 1} className="grid h-9 w-9 place-items-center text-neutral-700 disabled:text-neutral-300" aria-label={`Decrease ${itemName(item)}`}>
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="w-7 text-center text-[13px] font-semibold">{item.quantity}</span>
                          <button type="button" onClick={() => increase(item)} className="grid h-9 w-9 place-items-center text-neutral-700" aria-label={`Increase ${itemName(item)}`}>
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => beginEdit(item)} className="grid h-9 w-9 place-items-center rounded-lg text-neutral-500 hover:bg-neutral-100" aria-label={`Edit ${itemName(item)}`}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => removeFromCart(item)} className="grid h-9 w-9 place-items-center rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-red-600" aria-label={`Remove ${itemName(item)}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>

        {cartItems.length > 0 && (
          <footer className="shrink-0 border-t border-neutral-200 bg-white px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-4 sm:px-5">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[13px] text-neutral-500">Total</span>
              <strong className="text-[18px] text-neutral-950">{formatCurrency(Number(total || 0))}</strong>
            </div>
            <button
              type="button"
              onClick={() => setShowPayment(true)}
              disabled={showPayment}
              className="flex h-12 w-full items-center justify-center rounded-xl px-4 text-[14px] font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: accentColor }}
            >
              {showPayment ? 'Opening checkout...' : 'Continue to checkout'}
            </button>
          </footer>
        )}
          </>
        )}
      </section>

      {editingItem && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/45 sm:items-center sm:px-4">
          <div className="h-[92dvh] w-full overflow-hidden rounded-t-2xl bg-white sm:max-w-md sm:rounded-2xl">
            <ItemCustomization
              item={editingItem.item}
              initialSelections={editingItem.initialSelections}
              initialInstructions={editingItem.initialInstructions}
              initialQuantity={editingItem.initialQuantity}
              onClose={() => setEditingItem(null)}
              onSave={saveEdit}
            />
          </div>
        </div>
      )}

    </div>
  );
}
